import { useMemo, useState } from 'react';
import { formatDistance, formatDuration } from '../lib/geo';
import { buildGmapsDirUrl } from '../lib/gmaps';
import { buildGpx } from '../lib/gpx';
import { cardinalFromAzimuth } from '../lib/sun';
import { fmtTime } from '../lib/time';
import { useAppStore } from '../state/store';
import { PHASE_LABELS } from '../workers/protocol';
import {
  IconCopy,
  IconDownload,
  IconExternal,
  IconMoon,
  IconNavigate,
  IconShare,
  IconSun,
  IconWarning,
} from './Icons';
import { ShadeRing } from './ShadeRing';
import { ShadeTimeline } from './ShadeTimeline';

function downloadFile(name: string, mime: string, content: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function LoadingCard() {
  const phase = useAppStore((s) => s.phase);
  return (
    <section className="card summary summary--loading" role="status" data-peek>
      <div className="summary__hero">
        <div className="skeleton" style={{ width: 84, height: 84, borderRadius: '50%' }} />
        <div className="grow stack" style={{ gap: 8 }}>
          <div className="skeleton" style={{ width: '55%', height: 26 }} />
          <div className="skeleton" style={{ width: '80%', height: 14 }} />
          <div className="skeleton" style={{ width: '40%', height: 14 }} />
        </div>
      </div>
      <div className="progress" aria-hidden="true">
        <div className="progress__fill" />
      </div>
      <p className="summary__phase">{phase ? PHASE_LABELS[phase] : 'Preparo il calcolo…'}</p>
      {phase === 'download' && (
        <p className="t-cap">
          I dati arrivano dai server gratuiti di OpenStreetMap: la prima volta può richiedere
          qualche secondo.
        </p>
      )}
    </section>
  );
}

function ErrorCard() {
  const error = useAppStore((s) => s.error);
  const compute = useAppStore((s) => s.compute);
  return (
    <section className="card summary" data-peek>
      <div className="callout callout--error" role="alert">
        <span className="callout__icon">
          <IconWarning size={18} />
        </span>
        <div className="grow">
          <p>{error}</p>
          <button type="button" className="btn btn--xs btn--danger" onClick={() => void compute()}>
            Riprova
          </button>
        </div>
      </div>
    </section>
  );
}

/** Riepilogo del percorso calcolato: ombra, tempi, azioni, alternative. */
export function RouteSummary() {
  const status = useAppStore((s) => s.status);
  const plan = useAppStore((s) => s.plan);
  const meta = useAppStore((s) => s.meta);
  const planDepartMs = useAppStore((s) => s.planDepartMs);
  const applyAlpha = useAppStore((s) => s.applyAlpha);
  const startNav = useAppStore((s) => s.startNav);
  const setHint = useAppStore((s) => s.setHint);
  const [moreOpen, setMoreOpen] = useState(false);

  const mapsUrl = useMemo(() => {
    if (!plan || plan.chosen.coords.length < 2) return null;
    try {
      return buildGmapsDirUrl(
        plan.chosen.coords,
        plan.exportNeedsFallback ? undefined : plan.exportWaypoints,
      );
    } catch {
      return null;
    }
  }, [plan]);

  if (status === 'loading') return <LoadingCard />;
  if (status === 'error') return <ErrorCard />;
  if (status !== 'done' || !plan || planDepartMs === null) return null;

  const { chosen, shortest, pareto, night, sunAtDepart } = plan;
  const shadePct = Math.round(chosen.shadeFrac * 100);
  const shortestPct = Math.round(shortest.shadeFrac * 100);
  const extraPct = Math.round((chosen.lengthM / shortest.lengthM - 1) * 100);
  const sameAsShortest = Math.abs(chosen.lengthM - shortest.lengthM) < 1;
  const arrivalMs = planDepartMs + chosen.durationS * 1000;
  const nWaypoints = plan.exportWaypoints.length;

  const share = async () => {
    const url = window.location.href;
    const text = `Percorso a piedi con il ${shadePct}% di ombra (${formatDistance(chosen.lengthM)}, ${formatDuration(chosen.durationS)}) — ShadowRoute`;
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: 'ShadowRoute', text, url });
        return;
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
      }
    }
    const ok = await copyText(url);
    setHint(ok ? 'Link del percorso copiato!' : 'Copia non riuscita.');
  };

  const stamp = new Date(planDepartMs);
  const gpxName = `shadowroute-${stamp.toISOString().slice(0, 16).replace(/[T:]/g, '-')}.gpx`;

  return (
    <section className="card summary" aria-label="Percorso calcolato">
      <div data-peek>
        <div className="summary__hero">
          <ShadeRing frac={chosen.shadeFrac} />
          <div className="grow">
            <div className="summary__time t-num">{formatDuration(chosen.durationS)}</div>
            <div className="summary__sub t-sub t-num">
              {formatDistance(chosen.lengthM)} · arrivo {fmtTime(arrivalMs)}
              {!sameAsShortest && extraPct > 0 && ` · +${extraPct}% rispetto al più breve`}
            </div>
            {night ? (
              <span className="badge badge--night">
                <IconMoon size={13} /> Sole sotto l’orizzonte: via più breve
              </span>
            ) : (
              <span className="summary__sun">
                <IconSun size={14} /> Sole a {Math.round(sunAtDepart.elevationDeg)}° da{' '}
                {cardinalFromAzimuth(sunAtDepart.azimuthDeg)} · segue il sole mentre cammini
              </span>
            )}
          </div>
        </div>

        <div className="summary__actions">
          <button
            type="button"
            className="btn btn--primary"
            onClick={startNav}
            title="Naviga lungo il percorso con la tua posizione"
          >
            <IconNavigate size={18} /> Avvia
          </button>
          {mapsUrl && (
            <a
              className="btn btn--tinted summary__maps"
              href={mapsUrl}
              target="_blank"
              rel="noreferrer noopener"
              title="Apre Google Maps con questo percorso"
            >
              <IconExternal size={17} /> Google Maps
            </a>
          )}
          <button
            type="button"
            className="btn btn--plain summary__share"
            aria-label="Condividi il percorso"
            title="Condividi il percorso"
            onClick={() => void share()}
          >
            <IconShare size={18} />
          </button>
        </div>
      </div>

      <ShadeTimeline segments={chosen.segments} />

      <p className="summary__compare">
        {sameAsShortest
          ? night
            ? 'Di notte l’ombra non conta: ti porto per la strada più breve.'
            : 'Qui il percorso più breve è già il miglior compromesso: le deviazioni disponibili non renderebbero abbastanza ombra.'
          : `Il percorso più breve (${formatDistance(shortest.lengthM)}, tratteggiato) resterebbe in ombra solo per il ${shortestPct}%: con il ${extraPct}% di strada in più passi dal ${shortestPct}% al ${shadePct}%.`}
      </p>

      {pareto.length > 1 && (
        <div className="pareto">
          <span className="pareto__label">Alternative · distanza extra → ombra</span>
          <div className="chips">
            {pareto.map((p) => (
              <button
                key={`${p.alpha}-${Math.round(p.lengthM)}`}
                type="button"
                className={`chip chip--pareto${p.chosen ? ' chip--active' : ''}`}
                title={`${formatDistance(p.lengthM)} · ${formatDuration(p.durationS)}`}
                onClick={() => applyAlpha(p.alpha)}
              >
                +{Math.max(0, Math.round(p.extraLengthPct))}% → {Math.round(p.shadeFrac * 100)}%
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="summary__secondary">
        <button
          type="button"
          className="btn btn--xs btn--plain"
          onClick={() => setMoreOpen((o) => !o)}
          aria-expanded={moreOpen}
        >
          {moreOpen ? 'Meno opzioni' : 'Altre opzioni'}
        </button>
        {moreOpen && mapsUrl && (
          <>
            <button
              type="button"
              className="btn btn--xs btn--plain"
              title="Copia il link Google Maps"
              onClick={() => {
                void copyText(mapsUrl).then((ok) =>
                  setHint(
                    ok
                      ? 'Link Google Maps copiato!'
                      : 'Copia non riuscita: apri il link e copialo dalla barra.',
                  ),
                );
              }}
            >
              <IconCopy size={14} /> Link Maps
            </button>
            <button
              type="button"
              className="btn btn--xs btn--plain"
              title="Scarica il percorso esatto in GPX (OsmAnd, Komoot, smartwatch…)"
              onClick={() =>
                downloadFile(
                  gpxName,
                  'application/gpx+xml',
                  buildGpx('ShadowRoute — percorso all’ombra', chosen.coords, planDepartMs),
                )
              }
            >
              <IconDownload size={14} /> GPX
            </button>
          </>
        )}
      </div>

      {mapsUrl && (
        <p className="summary__note">
          {plan.exportNeedsFallback
            ? 'In Google Maps il percorso è approssimato con 9 punti di passaggio: su tratti lunghi può deviare un po’ dal tracciato colorato. Il GPX è esatto.'
            : nWaypoints === 0
              ? 'In Google Maps ottieni lo stesso tragitto, senza tappe intermedie.'
              : `In Google Maps il percorso appare come un tragitto unico con ${nWaypoints} ${nWaypoints === 1 ? 'punto di passaggio' : 'punti di passaggio'}: il minimo perché passi dalle strade all’ombra.`}
        </p>
      )}

      {meta && (
        <p className="summary__meta">
          {meta.buildings.toLocaleString('it-IT')} edifici ·{' '}
          {Math.round(meta.trees).toLocaleString('it-IT')} alberi ·{' '}
          {meta.edges.toLocaleString('it-IT')} tratti analizzati
          {meta.fromCache ? ' · dati in cache' : ''} · {(meta.routeMs / 1000).toFixed(1)} s
        </p>
      )}
    </section>
  );
}
